// src/api/threadApi.js
import cacheManager from "../utils/thread/cacheManager";
import { apiOptimizer } from "../utils/thread/apiOptimization";

// Create a singleton to hold the Auth0 functions
let _getAccessTokenSilently = null;

/**
 * Initialize the thread API with Auth0 functions
 * This should be called from a component that has access to useAuth0
 */
export const initializeThreadApi = (getAccessTokenSilently) => {
  _getAccessTokenSilently = getAccessTokenSilently;
};

/**
 * ThreadAPI service class for managing fight discussion threads
 * Handles authentication, error handling, and API communication
 * Requirements: 6.1, 6.4, 8.3
 */
class ThreadAPI {
  constructor() {
    // Use environment variable for API base URL, fallback to design document URL
    this.baseURL = process.env.REACT_APP_THREAD_API_URL || "";

    // Configuration from design document
    this.config = {
      maxMessageLength: 500,
      paginationLimits: {
        messages: 50,
        replies: 20,
      },
      retryConfig: {
        maxRetries: 3,
        backoffMultiplier: 2,
      },
      timeout: 15000, // 15 seconds
    };

    // Request deduplication cache
    this.pendingRequests = new Map();
  }

  /**
   * Get authentication token using Auth0
   * @param {boolean} forceRefresh - Force token refresh (not used with access tokens)
   * @returns {Promise<string>} Access token
   */
  async getAuthToken(forceRefresh = false) {
    if (!_getAccessTokenSilently) {
      throw new Error(
        "ThreadAPI not initialized. Call initializeThreadApi() first."
      );
    }

    try {
      return await _getAccessTokenSilently({
        audience: "https://api.dayshiftsports.com",
        scope: "openid profile email",
      });
    } catch (error) {
      throw new Error(`Authentication failed: ${error.message}`);
    }
  }

  /**
   * Base request method with authentication and error handling
   * @param {string} endpoint - API endpoint path
   * @param {Object} options - Request options
   * @param {boolean} useAuth - Whether to include authentication (default: true)
   * @returns {Promise<any>} API response data
   */
  async request(endpoint, options = {}, useAuth = true) {
    const url = `${this.baseURL}${endpoint}`;
    const startTime = Date.now();

    // Create request configuration
    const config = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
      ...options,
    };

    // Add authentication if required
    if (useAuth) {
      try {
        const token = await this.getAuthToken();
        config.headers.Authorization = `Bearer ${token}`;
      } catch (authError) {
        throw this.createError(
          "AUTH_ERROR",
          "Authentication required - please log in",
          401,
          authError
        );
      }
    }

    // Create AbortController for timeout handling
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);
    config.signal = controller.signal;

    try {
      const response = await fetch(url, config);
      clearTimeout(timeoutId);

      // Log successful requests in development
      if (process.env.NODE_ENV === "development") {
        const duration = Date.now() - startTime;
        console.log(
          `✅ ThreadAPI Success: ${config.method} ${endpoint} (${duration}ms)`
        );
      }

      // Handle non-2xx responses
      if (!response.ok) {
        const errorData = await this.parseErrorResponse(response);
        throw this.createError(
          this.getErrorType(response.status),
          errorData.message || this.getDefaultErrorMessage(response.status),
          response.status,
          errorData
        );
      }

      // Parse and return response data
      const data = await response.json();
      return data;
    } catch (error) {
      clearTimeout(timeoutId);

      // Handle different error types
      if (error.name === "AbortError") {
        throw this.createError(
          "TIMEOUT_ERROR",
          "Request timeout - please check your connection",
          408
        );
      }

      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw this.createError(
          "NETWORK_ERROR",
          "Network error - please check your internet connection",
          0
        );
      }

      // Re-throw ThreadAPI errors
      if (error.type) {
        throw error;
      }

      // Handle unknown errors
      throw this.createError(
        "UNKNOWN_ERROR",
        `Request failed: ${error.message}`,
        0,
        error
      );
    }
  }

  /**
   * Parse error response from API
   * @param {Response} response - Fetch response object
   * @returns {Promise<Object>} Parsed error data
   */
  async parseErrorResponse(response) {
    try {
      return await response.json();
    } catch {
      return { message: response.statusText || "Unknown error" };
    }
  }

  /**
   * Create standardized error object
   * @param {string} type - Error type
   * @param {string} message - Error message
   * @param {number} status - HTTP status code
   * @param {any} originalError - Original error object
   * @returns {Error} Standardized error
   */
  createError(type, message, status, originalError = null) {
    const error = new Error(message);
    error.type = type;
    error.status = status;
    error.originalError = originalError;
    error.timestamp = new Date().toISOString();

    // Log error for debugging
    if (process.env.NODE_ENV === "development") {
      console.error(`❌ ThreadAPI Error [${type}]:`, {
        message,
        status,
        originalError,
      });
    }

    return error;
  }

  /**
   * Get error type based on HTTP status code
   * @param {number} status - HTTP status code
   * @returns {string} Error type
   */
  getErrorType(status) {
    if (status === 401) return "AUTH_ERROR";
    if (status === 404) return "NOT_FOUND_ERROR";
    if (status === 429) return "RATE_LIMIT_ERROR";
    if (status >= 400 && status < 500) return "CLIENT_ERROR";
    if (status >= 500) return "SERVER_ERROR";
    return "UNKNOWN_ERROR";
  }

  /**
   * Get default error message for HTTP status code
   * @param {number} status - HTTP status code
   * @returns {string} Default error message
   */
  getDefaultErrorMessage(status) {
    switch (status) {
      case 401:
        return "Authentication required - please log in";
      case 404:
        return "The requested resource was not found";
      case 429:
        return "Too many requests - please wait before trying again";
      case 500:
        return "Server error - please try again later";
      default:
        return status >= 400 && status < 500
          ? "Invalid request"
          : "An unexpected error occurred";
    }
  }

  /**
   * Retry request with exponential backoff
   * @param {Function} requestFn - Function that makes the request
   * @param {number} maxRetries - Maximum number of retries
   * @param {number} baseDelay - Base delay in milliseconds
   * @returns {Promise<any>} Request result
   */
  async retryWithBackoff(
    requestFn,
    maxRetries = this.config.retryConfig.maxRetries,
    baseDelay = 1000
  ) {
    let lastError;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await requestFn();
      } catch (error) {
        lastError = error;

        // Don't retry certain error types
        if (error.type === "AUTH_ERROR" || error.type === "CLIENT_ERROR") {
          throw error;
        }

        // Don't retry on last attempt
        if (attempt === maxRetries) {
          break;
        }

        // Calculate delay with exponential backoff
        const delay =
          baseDelay *
          Math.pow(this.config.retryConfig.backoffMultiplier, attempt);
        await this.sleep(delay);

        if (process.env.NODE_ENV === "development") {
          console.log(
            `🔄 ThreadAPI Retry attempt ${
              attempt + 1
            }/${maxRetries} after ${delay}ms`
          );
        }
      }
    }

    throw lastError;
  }

  /**
   * Sleep utility for retry delays
   * @param {number} ms - Milliseconds to sleep
   * @returns {Promise<void>}
   */
  sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Handle request deduplication
   * @param {string} key - Request key for deduplication
   * @param {Function} requestFn - Function that makes the request
   * @returns {Promise<any>} Request result
   */
  async deduplicateRequest(key, requestFn) {
    // Check if request is already pending
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key);
    }

    // Create new request promise
    const requestPromise = requestFn().finally(() => {
      // Clean up pending request
      this.pendingRequests.delete(key);
    });

    // Store pending request
    this.pendingRequests.set(key, requestPromise);

    return requestPromise;
  }

  /**
   * Validate pagination parameters
   * @param {number} page - Page number
   * @param {number} limit - Items per page
   * @param {number} maxLimit - Maximum allowed limit
   * @throws {Error} If parameters are invalid
   */
  validatePagination(page, limit, maxLimit) {
    if (page < 1) {
      throw new Error("Page number must be greater than 0");
    }

    if (limit < 1) {
      throw new Error("Limit must be greater than 0");
    }

    if (limit > maxLimit) {
      throw new Error(`Limit cannot exceed ${maxLimit}`);
    }
  }

  /**
   * Build query string from parameters
   * @param {Object} params - Query parameters
   * @returns {string} Query string
   */
  buildQueryString(params) {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        searchParams.append(key, value.toString());
      }
    });

    const queryString = searchParams.toString();
    return queryString ? `?${queryString}` : "";
  }

  // ========================================
  // THREAD ACCESS METHODS
  // ========================================

  /**
   * Get thread by ID directly (for notification navigation)
   * Requirements: 2.1, 2.3, 7.1, 7.5
   *
   * @param {string} threadId - The thread ID to get
   * @param {boolean} useCache - Whether to use cache (default: true)
   * @returns {Promise<Object>} Thread response with thread, fight, and event data
   * @throws {Error} If threadId is invalid or API request fails
   */
  async getThreadById(threadId, useCache = true) {
    // Validate threadId parameter
    if (!threadId || typeof threadId !== "string") {
      throw this.createError(
        "CLIENT_ERROR",
        "Valid thread ID is required",
        400
      );
    }

    // Check cache first if enabled
    if (useCache) {
      const cachedData = cacheManager.getCachedThread(threadId);
      if (cachedData) {
        if (process.env.NODE_ENV === "development") {
          console.log(`🎯 Thread cache hit: ${threadId}`);
        }
        return cachedData;
      }
    }

    const endpoint = `/threads/${threadId}`;
    const optimizationKey = `getThreadById_${threadId}`;

    try {
      // Use API optimization for request deduplication and performance monitoring
      const response = await apiOptimizer.optimizeRequest(
        optimizationKey,
        async () => {
          // Use retry logic for reliability
          return await this.retryWithBackoff(async () => {
            const response = await this.request(endpoint);

            // Validate response structure
            this.validateFightThreadResponse(response);

            // Parse and enhance response data
            return this.parseFightThreadResponse(response);
          });
        },
        {
          cacheTime: 30000, // 30 seconds deduplication cache
          dataSize: 2048, // Estimated response size for performance monitoring
        }
      );

      // Cache the response if caching is enabled
      if (useCache && response.thread?.id) {
        cacheManager.cacheThread(threadId, response);

        // Also cache by fight ID if available
        if (response.fight?.FightId) {
          const fightCacheKey = `fight_${response.fight.FightId}`;
          cacheManager.cacheThread(fightCacheKey, response);
        }
      }

      return response;
    } catch (error) {
      // Handle specific error cases for thread access
      if (error.status === 404) {
        throw this.createError(
          "NOT_FOUND_ERROR",
          "Thread not found or discussion not available",
          404,
          error
        );
      }

      if (error.status === 403) {
        throw this.createError(
          "AUTH_ERROR",
          "You do not have permission to access this thread",
          403,
          error
        );
      }

      // Re-throw other errors
      throw error;
    }
  }

  // ========================================
  // FIGHT THREAD ACCESS METHODS
  // ========================================

  /**
   * Get or create fight thread for a specific fight
   * Primary endpoint for accessing fight threads with auto-creation
   * Requirements: 1.1, 1.2, 1.3, 1.4, 1.5
   *
   * @param {number} fightId - The fight ID to get/create thread for
   * @param {boolean} useCache - Whether to use cache (default: true)
   * @returns {Promise<Object>} Fight thread response with thread, fight, and event data
   * @throws {Error} If fightId is invalid or API request fails
   */
  async getFightThread(fightId, useCache = true) {
    // Validate fightId parameter
    if (!fightId || typeof fightId !== "number" || fightId <= 0) {
      throw this.createError("CLIENT_ERROR", "Valid fight ID is required", 400);
    }

    // Check cache first if enabled
    if (useCache) {
      const cacheKey = `fight_${fightId}`;
      const cachedData = cacheManager.getCachedThread(cacheKey);
      if (cachedData) {
        if (process.env.NODE_ENV === "development") {
          console.log(`🎯 Fight thread cache hit: ${fightId}`);
        }
        return cachedData;
      }
    }

    const endpoint = `/fights/${fightId}/thread`;
    const optimizationKey = `getFightThread_${fightId}`;

    try {
      // Use API optimization for request deduplication and performance monitoring
      const response = await apiOptimizer.optimizeRequest(
        optimizationKey,
        async () => {
          // Use retry logic for reliability
          return await this.retryWithBackoff(async () => {
            const response = await this.request(endpoint);

            // Validate response structure
            this.validateFightThreadResponse(response);

            // Parse and enhance response data
            return this.parseFightThreadResponse(response);
          });
        },
        {
          cacheTime: 30000, // 30 seconds deduplication cache
          dataSize: 2048, // Estimated response size for performance monitoring
        }
      );

      // Cache the response if caching is enabled
      if (useCache && response.thread?.id) {
        const cacheKey = `fight_${fightId}`;
        cacheManager.cacheThread(cacheKey, response);

        // Also cache by thread ID for direct access
        cacheManager.cacheThread(response.thread.id, response);
      }

      return response;
    } catch (error) {
      // Handle specific error cases for fight thread access
      if (error.status === 404) {
        throw this.createError(
          "NOT_FOUND_ERROR",
          "Fight not found or discussion not available",
          404,
          error
        );
      }

      if (error.status === 403) {
        throw this.createError(
          "AUTH_ERROR",
          "You do not have permission to access this fight discussion",
          403,
          error
        );
      }

      // Re-throw other errors
      throw error;
    }
  }

  /**
   * Validate fight thread response structure
   * @param {Object} response - API response to validate
   * @throws {Error} If response structure is invalid
   */
  validateFightThreadResponse(response) {
    if (!response || typeof response !== "object") {
      throw this.createError(
        "SERVER_ERROR",
        "Invalid response format from server",
        500
      );
    }

    // Check required fields
    const requiredFields = ["thread"];
    const missingFields = requiredFields.filter((field) => !response[field]);

    if (missingFields.length > 0) {
      throw this.createError(
        "SERVER_ERROR",
        `Missing required fields in response: ${missingFields.join(", ")}`,
        500
      );
    }

    // Validate thread structure
    const thread = response.thread;
    if (!thread.id || !thread.title || typeof thread.isOfficial !== "boolean") {
      throw this.createError(
        "SERVER_ERROR",
        "Invalid thread structure in response",
        500
      );
    }
  }

  /**
   * Parse and enhance fight thread response
   * @param {Object} response - Raw API response
   * @returns {Object} Parsed and enhanced response
   */
  parseFightThreadResponse(response) {
    const { thread, fight, event, created } = response;

    // Parse thread data
    const parsedThread = {
      ...thread,
      // Ensure dates are properly formatted
      createdAt: thread.createdAt
        ? new Date(thread.createdAt).toISOString()
        : null,
      updatedAt: thread.updatedAt
        ? new Date(thread.updatedAt).toISOString()
        : null,
      lastActivity: thread.lastActivity
        ? new Date(thread.lastActivity).toISOString()
        : null,
      // Ensure counts are numbers
      messageCount: parseInt(thread.messageCount) || 0,
      participantCount: parseInt(thread.participantCount) || 0,
    };

    // Parse fight data if available
    const parsedFight = fight
      ? {
          ...fight,
          FightId: parseInt(fight.FightId) || null,
          Fighters: Array.isArray(fight.Fighters)
            ? fight.Fighters.map((fighter) => ({
                ...fighter,
                Name: fighter.Name || "",
                FirstName: fighter.FirstName || "",
                LastName: fighter.LastName || "",
                WeightClass: fighter.WeightClass || "",
              }))
            : [],
        }
      : null;

    // Parse event data if available
    const parsedEvent = event
      ? {
          ...event,
          name: event.name || "",
          dateTime: event.dateTime
            ? new Date(event.dateTime).toISOString()
            : null,
        }
      : null;

    // Build enhanced response
    const enhancedResponse = {
      thread: parsedThread,
      fight: parsedFight,
      event: parsedEvent,
      created: Boolean(created),
      // Add metadata for UI usage
      metadata: {
        fightTitle: this.buildFightTitle(parsedFight),
        eventTitle: parsedEvent?.name || "Unknown Event",
        isNewThread: Boolean(created),
        accessedAt: new Date().toISOString(),
      },
    };

    // Log successful thread access in development
    if (process.env.NODE_ENV === "development") {
      console.log("🥊 Fight Thread Accessed:", {
        threadId: parsedThread.id,
        fightId: parsedFight?.FightId,
        created: created,
        title: enhancedResponse.metadata.fightTitle,
      });
    }

    return enhancedResponse;
  }

  /**
   * Build fight title from fighter data
   * @param {Object} fight - Fight data with fighters
   * @returns {string} Formatted fight title
   */
  buildFightTitle(fight) {
    if (!fight || !Array.isArray(fight.Fighters) || fight.Fighters.length < 2) {
      return "Fight Discussion";
    }

    const [fighter1, fighter2] = fight.Fighters;
    const name1 =
      fighter1.Name ||
      `${fighter1.FirstName} ${fighter1.LastName}`.trim() ||
      "Fighter 1";
    const name2 =
      fighter2.Name ||
      `${fighter2.FirstName} ${fighter2.LastName}`.trim() ||
      "Fighter 2";

    return `${name1} vs ${name2}`;
  }

  // ========================================
  // MESSAGE MANAGEMENT METHODS
  // ========================================

  /**
   * Get messages for a thread with pagination support
   * Requirements: 3.1, 3.2, 6.1, 8.1
   *
   * @param {string} threadId - The thread ID to get messages for
   * @param {number} page - Page number (1-based)
   * @param {number} limit - Number of messages per page (max 50, recommended 10-15 for performance)
   * @param {boolean} useCache - Whether to use cache (default: true)
   * @param {Object} optimizationHints - Hints for pagination optimization
   * @returns {Promise<Object>} Messages response with pagination data
   * @throws {Error} If parameters are invalid or API request fails
   */
  async getMessages(
    threadId,
    page = 1,
    limit = 10,
    useCache = true,
    optimizationHints = {}
  ) {
    // Validate parameters
    if (!threadId || typeof threadId !== "string") {
      throw this.createError(
        "CLIENT_ERROR",
        "Valid thread ID is required",
        400
      );
    }

    // Optimize pagination parameters if hints are provided
    let optimizedLimit = limit;
    if (optimizationHints.viewportHeight && optimizationHints.itemHeight) {
      optimizedLimit = apiOptimizer.pagination.calculateOptimalPageSize(
        optimizationHints.viewportHeight,
        optimizationHints.itemHeight,
        apiOptimizer.network.getNetworkSpeed()
      );
      // Use the larger of requested limit or optimized limit
      optimizedLimit = Math.max(limit, optimizedLimit);
    }

    // Validate pagination parameters
    this.validatePagination(
      page,
      optimizedLimit,
      this.config.paginationLimits.messages
    );

    // Check cache first if enabled and it's the first page
    if (useCache && page === 1) {
      const cachedData = cacheManager.getCachedMessages(threadId);
      if (cachedData && cachedData.pagination?.limit >= optimizedLimit) {
        // Return subset of cached data if we have enough
        const messages = cachedData.messages.slice(0, optimizedLimit);
        const response = {
          ...cachedData,
          messages,
          pagination: {
            ...cachedData.pagination,
            limit: optimizedLimit,
            currentCount: messages.length,
          },
        };

        if (process.env.NODE_ENV === "development") {
          console.log(
            `💬 Messages cache hit: ${threadId} (page ${page}, optimized limit: ${optimizedLimit})`
          );
        }

        return response;
      }
    }

    // Use optimized pagination request
    try {
      const response = await apiOptimizer.optimizePaginationRequest(
        {
          page,
          limit: optimizedLimit,
          viewportHeight: optimizationHints.viewportHeight,
          itemHeight: optimizationHints.itemHeight,
        },
        async (optimizedPage, finalLimit) => {
          // Convert page to skip for backend API
          const skip = (optimizedPage - 1) * finalLimit;
          const queryParams = this.buildQueryString({
            limit: finalLimit,
            skip,
            sort: "desc", // Changed to desc to get newest messages first
            includeReplies: "true",
          });
          const endpoint = `/threads/${threadId}/messages${queryParams}`;

          // Use retry logic for reliability
          return await this.retryWithBackoff(async () => {
            const response = await this.request(endpoint);

            // Validate response structure
            this.validateMessagesResponse(response);

            // Parse and enhance response data
            return this.parseMessagesResponse(
              response,
              threadId,
              optimizedPage,
              finalLimit
            );
          });
        }
      );

      // Cache the response if caching is enabled and it's the first page
      if (useCache && page === 1 && response.messages) {
        cacheManager.cacheMessages(
          threadId,
          response.messages,
          response.pagination
        );
      }

      return response;
    } catch (error) {
      // Handle specific error cases for message retrieval
      if (error.status === 404) {
        throw this.createError(
          "NOT_FOUND_ERROR",
          "Thread not found or no messages available",
          404,
          error
        );
      }

      if (error.status === 403) {
        throw this.createError(
          "AUTH_ERROR",
          "You do not have permission to view messages in this thread",
          403,
          error
        );
      }

      // Re-throw other errors
      throw error;
    }
  }

  /**
   * Post a new message to a thread
   * Requirements: 3.1, 3.2, 6.1, 8.1
   *
   * @param {string} threadId - The thread ID to post message to
   * @param {string} content - Message content
   * @returns {Promise<Object>} Posted message data
   * @throws {Error} If parameters are invalid or API request fails
   */
  async postMessage(threadId, content) {
    // Validate parameters
    if (!threadId || typeof threadId !== "string") {
      throw this.createError(
        "CLIENT_ERROR",
        "Valid thread ID is required",
        400
      );
    }

    if (!content || typeof content !== "string") {
      throw this.createError(
        "CLIENT_ERROR",
        "Message content is required",
        400
      );
    }

    // Validate content length
    const trimmedContent = content.trim();
    if (trimmedContent.length === 0) {
      throw this.createError(
        "CLIENT_ERROR",
        "Message content cannot be empty",
        400
      );
    }

    if (trimmedContent.length > this.config.maxMessageLength) {
      throw this.createError(
        "CLIENT_ERROR",
        `Message content cannot exceed ${this.config.maxMessageLength} characters`,
        400
      );
    }

    const endpoint = `/threads/${threadId}/messages`;
    const requestBody = { content: trimmedContent };

    try {
      // Don't use retry logic for POST requests to prevent duplicate messages
      const response = await this.request(endpoint, {
        method: "POST",
        body: JSON.stringify(requestBody),
      });

      // Validate response structure
      this.validateMessageResponse(response);

      // Parse and enhance response data
      const parsedMessage = this.parseMessageResponse(response);

      // Update cache with new message
      cacheManager.addMessageToCache(threadId, parsedMessage);

      // Invalidate thread cache to refresh message count
      const fightCacheKeys = Array.from(cacheManager.threadCache.keys()).filter(
        (key) => {
          const cached = cacheManager.getCachedThread(key);
          return cached?.thread?.id === threadId;
        }
      );

      fightCacheKeys.forEach((key) => {
        const cached = cacheManager.getCachedThread(key);
        if (cached) {
          // Update message count in cached thread
          const updatedThread = {
            ...cached.thread,
            messageCount: (cached.thread.messageCount || 0) + 1,
            lastActivity: new Date().toISOString(),
          };
          cacheManager.cacheThread(key, {
            ...cached,
            thread: updatedThread,
          });
        }
      });

      return parsedMessage;
    } catch (error) {
      // Handle specific error cases for message posting
      if (error.status === 404) {
        throw this.createError(
          "NOT_FOUND_ERROR",
          "Thread not found - cannot post message",
          404,
          error
        );
      }

      if (error.status === 403) {
        throw this.createError(
          "AUTH_ERROR",
          "You do not have permission to post messages in this thread",
          403,
          error
        );
      }

      if (error.status === 429) {
        throw this.createError(
          "RATE_LIMIT_ERROR",
          "You are posting messages too quickly - please wait before trying again",
          429,
          error
        );
      }

      // Re-throw other errors
      throw error;
    }
  }

  /**
   * Validate messages response structure
   * @param {Object} response - API response to validate
   * @throws {Error} If response structure is invalid
   */
  validateMessagesResponse(response) {
    if (!response || typeof response !== "object") {
      throw this.createError(
        "SERVER_ERROR",
        "Invalid response format from server",
        500
      );
    }

    // Check required fields for GET messages response
    if (!Array.isArray(response.messages)) {
      throw this.createError(
        "SERVER_ERROR",
        "Invalid messages array in response",
        500
      );
    }

    if (!response.pagination || typeof response.pagination !== "object") {
      throw this.createError(
        "SERVER_ERROR",
        "Missing pagination data in response",
        500
      );
    }

    // Validate pagination structure (API uses skip/count, not page/total)
    const { pagination } = response;
    const requiredPaginationFields = ["limit", "skip", "count", "hasMore"];
    const missingFields = requiredPaginationFields.filter(
      (field) => pagination[field] === undefined
    );

    if (missingFields.length > 0) {
      throw this.createError(
        "SERVER_ERROR",
        `Missing pagination fields: ${missingFields.join(", ")}`,
        500
      );
    }
  }

  /**
   * Validate single message response structure
   * @param {Object} response - API response to validate
   * @throws {Error} If response structure is invalid
   */
  validateMessageResponse(response) {
    if (!response || typeof response !== "object") {
      throw this.createError(
        "SERVER_ERROR",
        "Invalid response format from server",
        500
      );
    }

    // API returns { message: {...}, thread: {...} }
    const message = response.message;
    if (!message || typeof message !== "object") {
      throw this.createError(
        "SERVER_ERROR",
        "Missing message object in response",
        500
      );
    }

    // Check required fields for a message (API uses 'id', not '_id')
    const requiredFields = ["id", "content", "threadId", "author", "createdAt"];
    const missingFields = requiredFields.filter((field) => !message[field]);

    if (missingFields.length > 0) {
      throw this.createError(
        "SERVER_ERROR",
        `Missing required message fields: ${missingFields.join(", ")}`,
        500
      );
    }

    // Validate author structure
    if (!message.author || typeof message.author !== "object") {
      throw this.createError(
        "SERVER_ERROR",
        "Invalid author data in message response",
        500
      );
    }
  }

  /**
   * Parse and enhance messages response
   * @param {Object} response - Raw API response
   * @param {string} threadId - Thread ID for context
   * @param {number} page - Current page
   * @param {number} limit - Items per page
   * @returns {Object} Parsed and enhanced response
   */
  parseMessagesResponse(response, threadId, page, limit) {
    const { messages, pagination } = response;

    // Parse messages with enhanced data
    const parsedMessages = messages.map((message) =>
      this.parseMessageData(message)
    );

    // Transform backend pagination to frontend format
    const parsedPagination = {
      page: Math.floor((parseInt(pagination.skip) || 0) / limit) + 1,
      limit: parseInt(pagination.limit) || limit,
      total: parseInt(pagination.count) || 0,
      hasMore: Boolean(pagination.hasMore),
      // Calculate additional pagination info
      totalPages: Math.ceil((parseInt(pagination.count) || 0) / limit),
      currentCount: parsedMessages.length,
    };

    // Build enhanced response
    const enhancedResponse = {
      messages: parsedMessages,
      pagination: parsedPagination,
      // Add metadata for UI usage
      metadata: {
        threadId,
        fetchedAt: new Date().toISOString(),
        isEmpty: parsedMessages.length === 0,
        isFirstPage: page === 1,
        isLastPage: !parsedPagination.hasMore,
      },
    };

    // Log successful message fetch in development
    if (process.env.NODE_ENV === "development") {
      console.log("💬 Messages Fetched:", {
        threadId,
        page,
        count: parsedMessages.length,
        total: parsedPagination.total,
        hasMore: parsedPagination.hasMore,
      });
    }

    return enhancedResponse;
  }

  /**
   * Parse and enhance single message response
   * @param {Object} response - Raw API response
   * @returns {Object} Parsed and enhanced message
   */
  parseMessageResponse(response) {
    // Extract the message from the API response structure
    const message = response.message;
    const parsedMessage = this.parseMessageData(message);

    // Log successful message post in development
    if (process.env.NODE_ENV === "development") {
      console.log("✉️ Message Posted:", {
        messageId: parsedMessage._id,
        threadId: parsedMessage.threadId,
        author: parsedMessage.author.username,
        contentLength: parsedMessage.content.length,
      });
    }

    return parsedMessage;
  }

  /**
   * Parse individual message data with enhancements
   * @param {Object} message - Raw message data
   * @returns {Object} Parsed and enhanced message
   */
  parseMessageData(message) {
    return {
      // Map backend fields to frontend expected fields
      _id: message.id || message._id,
      threadId: message.threadId,
      content: message.content,
      parentMessageId: message.parentMessageId,

      // Ensure dates are properly formatted
      createdAt: message.createdAt
        ? new Date(message.createdAt).toISOString()
        : null,
      updatedAt: message.updatedAt
        ? new Date(message.updatedAt).toISOString()
        : null,

      // Ensure counts are numbers
      likeCount: parseInt(message.likeCount) || 0,
      replyCount: parseInt(message.replyCount) || 0,

      // Ensure arrays exist
      likes: Array.isArray(message.likes) ? message.likes : [],
      replies: Array.isArray(message.replies) ? message.replies : [],

      // Parse author data
      author: {
        _id: message.author._id || message.author.id || "",
        auth0Id: message.author.auth0Id || "",
        username: message.author.username || "Unknown User",
        name: message.author.name || message.author.username || "Unknown User",
        picture: message.author.picture || null,
      },

      // Add computed properties for UI usage
      isLiked: Boolean(message.hasUserLiked),
      hasUserLiked: Boolean(message.hasUserLiked), // Keep the original property name too
      canEdit: false, // Will be set by UI based on current user
      canDelete: false, // Will be set by UI based on current user
      displayTime: message.createdAt
        ? this.formatMessageTime(new Date(message.createdAt))
        : "Unknown time",
    };
  }

  /**
   * Format message timestamp for display
   * @param {Date} date - Message date
   * @returns {string} Formatted time string
   */
  formatMessageTime(date) {
    const now = new Date();
    const diffMs = now - date;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) {
      return "Just now";
    } else if (diffMinutes < 60) {
      return `${diffMinutes}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  }

  // ========================================
  // REPLY MANAGEMENT METHODS
  // ========================================

  /**
   * Get replies for a message with pagination support
   * Requirements: 3.3, 3.4, 6.1
   *
   * @param {string} messageId - The message ID to get replies for
   * @param {number} page - Page number (1-based)
   * @param {number} limit - Number of replies per page (max 20)
   * @returns {Promise<Object>} Replies response with pagination data
   * @throws {Error} If parameters are invalid or API request fails
   */
  async getReplies(messageId, page = 1, limit = 20) {
    // Validate parameters
    if (!messageId || typeof messageId !== "string") {
      throw this.createError(
        "CLIENT_ERROR",
        "Valid message ID is required",
        400
      );
    }

    // Validate pagination parameters
    this.validatePagination(page, limit, this.config.paginationLimits.replies);

    // Convert page to skip for backend API
    const skip = (page - 1) * limit;
    const queryParams = this.buildQueryString({
      limit,
      skip,
      sort: "asc",
    });
    const endpoint = `/messages/${messageId}/replies${queryParams}`;
    const requestKey = `getReplies_${messageId}_${page}_${limit}`;

    try {
      // Use request deduplication to prevent multiple simultaneous requests
      return await this.deduplicateRequest(requestKey, async () => {
        // Use retry logic for reliability
        return await this.retryWithBackoff(async () => {
          const response = await this.request(endpoint);

          // Validate response structure
          this.validateRepliesResponse(response);

          // Parse and enhance response data
          return this.parseRepliesResponse(response, messageId, page, limit);
        });
      });
    } catch (error) {
      // Handle specific error cases for reply retrieval
      if (error.status === 404) {
        throw this.createError(
          "NOT_FOUND_ERROR",
          "Message not found or no replies available",
          404,
          error
        );
      }

      if (error.status === 403) {
        throw this.createError(
          "AUTH_ERROR",
          "You do not have permission to view replies for this message",
          403,
          error
        );
      }

      // Re-throw other errors
      throw error;
    }
  }

  /**
   * Reply to a message
   * Requirements: 3.3, 3.4, 6.1
   *
   * @param {string} messageId - The message ID to reply to
   * @param {string} content - Reply content
   * @returns {Promise<Object>} Posted reply data
   * @throws {Error} If parameters are invalid or API request fails
   */
  async replyToMessage(messageId, content) {
    // Validate parameters
    if (!messageId || typeof messageId !== "string") {
      throw this.createError(
        "CLIENT_ERROR",
        "Valid message ID is required",
        400
      );
    }

    if (!content || typeof content !== "string") {
      throw this.createError("CLIENT_ERROR", "Reply content is required", 400);
    }

    // Validate content length
    const trimmedContent = content.trim();
    if (trimmedContent.length === 0) {
      throw this.createError(
        "CLIENT_ERROR",
        "Reply content cannot be empty",
        400
      );
    }

    if (trimmedContent.length > this.config.maxMessageLength) {
      throw this.createError(
        "CLIENT_ERROR",
        `Reply content cannot exceed ${this.config.maxMessageLength} characters`,
        400
      );
    }

    const endpoint = `/messages/${messageId}/replies`;
    const requestBody = { content: trimmedContent };

    try {
      // Don't use retry logic for POST requests to prevent duplicate replies
      const response = await this.request(endpoint, {
        method: "POST",
        body: JSON.stringify(requestBody),
      });

      // Validate response structure
      this.validateReplyResponse(response);

      // Parse and enhance response data
      return this.parseReplyResponse(response);
    } catch (error) {
      // Handle specific error cases for reply posting
      if (error.status === 404) {
        throw this.createError(
          "NOT_FOUND_ERROR",
          "Message not found - cannot post reply",
          404,
          error
        );
      }

      if (error.status === 403) {
        throw this.createError(
          "AUTH_ERROR",
          "You do not have permission to reply to this message",
          403,
          error
        );
      }

      if (error.status === 429) {
        throw this.createError(
          "RATE_LIMIT_ERROR",
          "You are posting replies too quickly - please wait before trying again",
          429,
          error
        );
      }

      // Re-throw other errors
      throw error;
    }
  }

  // ========================================
  // LIKE SYSTEM METHODS
  // ========================================

  /**
   * Toggle like status for a message
   * Requirements: 4.1, 4.2, 6.1
   *
   * @param {string} messageId - The message ID to like/unlike
   * @returns {Promise<Object>} Updated like status and count
   * @throws {Error} If messageId is invalid or API request fails
   */
  async likeMessage(messageId) {
    // Validate parameters
    if (!messageId || typeof messageId !== "string") {
      throw this.createError(
        "CLIENT_ERROR",
        "Valid message ID is required",
        400
      );
    }

    const endpoint = `/messages/${messageId}/like`;

    try {
      // Don't use retry logic for PUT requests to prevent duplicate likes
      const response = await this.request(endpoint, {
        method: "PUT",
      });

      // Validate response structure
      this.validateLikeResponse(response);

      // Parse and enhance response data
      return this.parseLikeResponse(response, "message", messageId);
    } catch (error) {
      // Handle specific error cases for message liking
      if (error.status === 404) {
        throw this.createError(
          "NOT_FOUND_ERROR",
          "Message not found - cannot toggle like",
          404,
          error
        );
      }

      if (error.status === 403) {
        throw this.createError(
          "AUTH_ERROR",
          "You do not have permission to like this message",
          403,
          error
        );
      }

      if (error.status === 409) {
        throw this.createError(
          "CLIENT_ERROR",
          "Cannot like your own message",
          409,
          error
        );
      }

      if (error.status === 429) {
        throw this.createError(
          "RATE_LIMIT_ERROR",
          "You are liking content too quickly - please wait before trying again",
          429,
          error
        );
      }

      // Re-throw other errors
      throw error;
    }
  }

  /**
   * Toggle like status for a reply
   * Note: Replies are stored as messages with parentMessageId, so we use the messages endpoint
   * Requirements: 4.1, 4.2, 6.1
   *
   * @param {string} replyId - The reply ID to like/unlike
   * @returns {Promise<Object>} Updated like status and count
   * @throws {Error} If replyId is invalid or API request fails
   */
  async likeReply(replyId) {
    // Validate parameters
    if (!replyId || typeof replyId !== "string") {
      throw this.createError("CLIENT_ERROR", "Valid reply ID is required", 400);
    }

    // Replies are stored as messages with parentMessageId, so use the messages endpoint
    const endpoint = `/messages/${replyId}/like`;

    try {
      // Don't use retry logic for PUT requests to prevent duplicate likes
      const response = await this.request(endpoint, {
        method: "PUT",
      });

      // Validate response structure
      this.validateLikeResponse(response);

      // Parse and enhance response data
      return this.parseLikeResponse(response, "reply", replyId);
    } catch (error) {
      // Handle specific error cases for reply liking
      if (error.status === 404) {
        throw this.createError(
          "NOT_FOUND_ERROR",
          "Reply not found - cannot toggle like",
          404,
          error
        );
      }

      if (error.status === 403) {
        throw this.createError(
          "AUTH_ERROR",
          "You do not have permission to like this reply",
          403,
          error
        );
      }

      if (error.status === 409) {
        throw this.createError(
          "CLIENT_ERROR",
          "Cannot like your own reply",
          409,
          error
        );
      }

      if (error.status === 429) {
        throw this.createError(
          "RATE_LIMIT_ERROR",
          "You are liking content too quickly - please wait before trying again",
          429,
          error
        );
      }

      // Re-throw other errors
      throw error;
    }
  }

  /**
   * Validate like response structure
   * @param {Object} response - API response to validate
   * @throws {Error} If response structure is invalid
   */
  validateLikeResponse(response) {
    if (!response || typeof response !== "object") {
      throw this.createError(
        "SERVER_ERROR",
        "Invalid response format from server",
        500
      );
    }

    // Check if response has the expected structure
    let likeData = response;

    // Check if the like data is nested (e.g., response.data, response.result, etc.)
    if (response.data && typeof response.data === "object") {
      likeData = response.data;
    } else if (response.result && typeof response.result === "object") {
      likeData = response.result;
    } else if (
      response.message &&
      typeof response.message === "object" &&
      response.message.hasUserLiked !== undefined
    ) {
      // Backend uses hasUserLiked instead of liked for messages
      likeData = {
        liked: Boolean(response.message.hasUserLiked),
        likeCount: parseInt(response.message.likeCount) || 0,
      };
    } else if (
      response.reply &&
      typeof response.reply === "object" &&
      response.reply.hasUserLiked !== undefined
    ) {
      // Backend uses hasUserLiked instead of liked for replies
      likeData = {
        liked: Boolean(response.reply.hasUserLiked),
        likeCount: parseInt(response.reply.likeCount) || 0,
      };
    }

    // Check required fields for like response
    const requiredFields = ["liked", "likeCount"];
    const missingFields = requiredFields.filter(
      (field) => likeData[field] === undefined
    );

    if (missingFields.length > 0) {
      throw this.createError(
        "SERVER_ERROR",
        `Missing required like response fields: ${missingFields.join(", ")}`,
        500
      );
    }

    // Validate field types
    if (typeof likeData.liked !== "boolean") {
      throw this.createError(
        "SERVER_ERROR",
        "Invalid liked status in response",
        500
      );
    }

    if (typeof likeData.likeCount !== "number" || likeData.likeCount < 0) {
      throw this.createError(
        "SERVER_ERROR",
        "Invalid like count in response",
        500
      );
    }

    // Store the correct like data structure for parsing
    response._likeData = likeData;
  }

  /**
   * Parse and enhance like response
   * @param {Object} response - Raw API response
   * @param {string} type - Type of content liked ("message" or "reply")
   * @param {string} contentId - ID of the liked content
   * @returns {Object} Parsed and enhanced like response
   */
  parseLikeResponse(response, type, contentId) {
    // Use the validated like data structure
    const likeData = response._likeData || response;
    const { liked, likeCount } = likeData;
    const { message, reply } = response;

    // Build enhanced response
    const enhancedResponse = {
      liked: Boolean(liked),
      likeCount: parseInt(likeCount) || 0,
      contentType: type,
      contentId,
      // Add metadata for UI usage
      metadata: {
        action: liked ? "liked" : "unliked",
        timestamp: new Date().toISOString(),
        previousCount: liked ? likeCount - 1 : likeCount + 1,
      },
    };

    // Include updated content if provided
    if (message) {
      enhancedResponse.updatedMessage = this.parseMessageData(message);
    }

    if (reply) {
      enhancedResponse.updatedReply = this.parseReplyData(reply);
    }

    // Log successful like action in development
    if (process.env.NODE_ENV === "development") {
      console.log(`👍 ${type} ${liked ? "Liked" : "Unliked"}:`, {
        contentId,
        likeCount,
        action: enhancedResponse.metadata.action,
      });
    }

    return enhancedResponse;
  }

  /**
   * Parse individual reply data with enhancements
   * @param {Object} reply - Raw reply data
   * @returns {Object} Parsed and enhanced reply
   */
  parseReplyData(reply) {
    return {
      // Map backend fields to frontend expected fields
      _id: reply.id || reply._id,
      parentMessageId: reply.parentMessageId,
      threadId: reply.threadId,
      content: reply.content,

      // Ensure dates are properly formatted
      createdAt: reply.createdAt
        ? new Date(reply.createdAt).toISOString()
        : null,
      updatedAt: reply.updatedAt
        ? new Date(reply.updatedAt).toISOString()
        : null,

      // Ensure counts are numbers
      likeCount: parseInt(reply.likeCount) || 0,

      // Ensure arrays exist
      likes: Array.isArray(reply.likes) ? reply.likes : [],

      // Parse author data
      author: {
        _id: reply.author._id || reply.author.id || "",
        auth0Id: reply.author.auth0Id || "",
        username: reply.author.username || "Unknown User",
        name: reply.author.name || reply.author.username || "Unknown User",
        picture: reply.author.picture || null,
      },

      // Add computed properties for UI usage
      isLiked: Boolean(reply.hasUserLiked),
      hasUserLiked: Boolean(reply.hasUserLiked), // Keep the original property name too
      canEdit: false, // Will be set by UI based on current user
      canDelete: false, // Will be set by UI based on current user
      displayTime: reply.createdAt
        ? this.formatMessageTime(new Date(reply.createdAt))
        : "Unknown time",
    };
  }

  /**
   * Validate replies response structure
   * @param {Object} response - API response to validate
   * @throws {Error} If response structure is invalid
   */
  validateRepliesResponse(response) {
    if (!response || typeof response !== "object") {
      throw this.createError(
        "SERVER_ERROR",
        "Invalid response format from server",
        500
      );
    }

    // Check required fields for GET replies response
    if (!Array.isArray(response.replies)) {
      throw this.createError(
        "SERVER_ERROR",
        "Invalid replies array in response",
        500
      );
    }

    if (!response.pagination || typeof response.pagination !== "object") {
      throw this.createError(
        "SERVER_ERROR",
        "Missing pagination data in response",
        500
      );
    }

    // Validate pagination structure
    const { pagination } = response;
    const requiredPaginationFields = ["limit", "skip", "count", "hasMore"];
    const missingFields = requiredPaginationFields.filter(
      (field) => pagination[field] === undefined
    );

    if (missingFields.length > 0) {
      throw this.createError(
        "SERVER_ERROR",
        `Missing pagination fields: ${missingFields.join(", ")}`,
        500
      );
    }
  }

  /**
   * Validate single reply response structure
   * @param {Object} response - API response to validate
   * @throws {Error} If response structure is invalid
   */
  validateReplyResponse(response) {
    if (!response || typeof response !== "object") {
      throw this.createError(
        "SERVER_ERROR",
        "Invalid response format from server",
        500
      );
    }

    // API returns { reply: {...}, message: {...} }
    const reply = response.reply;
    if (!reply || typeof reply !== "object") {
      throw this.createError(
        "SERVER_ERROR",
        "Missing reply object in response",
        500
      );
    }

    // Check required fields for a reply
    const requiredFields = [
      "id",
      "content",
      "parentMessageId",
      "threadId",
      "author",
      "createdAt",
    ];
    const missingFields = requiredFields.filter((field) => !reply[field]);

    if (missingFields.length > 0) {
      throw this.createError(
        "SERVER_ERROR",
        `Missing required reply fields: ${missingFields.join(", ")}`,
        500
      );
    }

    // Validate author structure
    if (!reply.author || typeof reply.author !== "object") {
      throw this.createError(
        "SERVER_ERROR",
        "Invalid author data in reply response",
        500
      );
    }
  }

  /**
   * Parse and enhance replies response
   * @param {Object} response - Raw API response
   * @param {string} messageId - Message ID for context
   * @param {number} page - Current page
   * @param {number} limit - Items per page
   * @returns {Object} Parsed and enhanced response
   */
  parseRepliesResponse(response, messageId, page, limit) {
    const { replies, pagination } = response;

    // Parse replies with enhanced data
    const parsedReplies = replies.map((reply) => this.parseReplyData(reply));

    // Transform backend pagination to frontend format
    const parsedPagination = {
      page: Math.floor((parseInt(pagination.skip) || 0) / limit) + 1,
      limit: parseInt(pagination.limit) || limit,
      total: parseInt(pagination.count) || 0,
      hasMore: Boolean(pagination.hasMore),
      // Calculate additional pagination info
      totalPages: Math.ceil((parseInt(pagination.count) || 0) / limit),
      currentCount: parsedReplies.length,
    };

    // Build enhanced response
    const enhancedResponse = {
      replies: parsedReplies,
      pagination: parsedPagination,
      // Add metadata for UI usage
      metadata: {
        messageId,
        fetchedAt: new Date().toISOString(),
        isEmpty: parsedReplies.length === 0,
        isFirstPage: page === 1,
        isLastPage: !parsedPagination.hasMore,
      },
    };

    // Log successful replies fetch in development
    if (process.env.NODE_ENV === "development") {
      console.log("💬 Replies Fetched:", {
        messageId,
        page,
        count: parsedReplies.length,
        total: parsedPagination.total,
        hasMore: parsedPagination.hasMore,
      });
    }

    return enhancedResponse;
  }

  /**
   * Parse and enhance single reply response
   * @param {Object} response - Raw API response
   * @returns {Object} Parsed and enhanced reply
   */
  parseReplyResponse(response) {
    // Extract the reply from the API response structure
    const reply = response.reply;
    const parsedReply = this.parseReplyData(reply);

    // Log successful reply post in development
    if (process.env.NODE_ENV === "development") {
      console.log("✉️ Reply Posted:", {
        replyId: parsedReply._id,
        parentMessageId: parsedReply.parentMessageId,
        threadId: parsedReply.threadId,
        author: parsedReply.author.username,
        contentLength: parsedReply.content.length,
      });
    }

    return parsedReply;
  }
  // ========================================
  // CACHE MANAGEMENT METHODS
  // ========================================

  /**
   * Get cache statistics
   * @returns {Object} Cache statistics
   */
  getCacheStats() {
    return cacheManager.getStats();
  }

  /**
   * Clear all caches
   */
  clearCache() {
    cacheManager.clearAll();
  }

  /**
   * Invalidate thread cache
   * @param {string} threadId - Thread ID to invalidate
   */
  invalidateThreadCache(threadId) {
    cacheManager.invalidateThread(threadId);
  }

  /**
   * Invalidate message cache
   * @param {string} messageId - Message ID to invalidate
   */
  invalidateMessageCache(messageId) {
    cacheManager.invalidateMessage(messageId);
  }

  /**
   * Update cached message (for likes, etc.)
   * @param {string} messageId - Message ID
   * @param {Object} updates - Updates to apply
   */
  updateCachedMessage(messageId, updates) {
    cacheManager.updateCachedMessage(messageId, updates);
  }

  /**
   * Get cached thread data
   * @param {string} threadId - Thread ID
   * @returns {Object|null} Cached thread data or null
   */
  getCachedThread(threadId) {
    return cacheManager.getCachedThread(threadId);
  }

  /**
   * Get cached messages for a thread
   * @param {string} threadId - Thread ID
   * @returns {Object|null} Cached messages data or null
   */
  getCachedMessages(threadId) {
    return cacheManager.getCachedMessages(threadId);
  }

  // ========================================
  // API OPTIMIZATION METHODS
  // ========================================

  /**
   * Get API optimization statistics
   * @returns {Object} API optimization statistics
   */
  getOptimizationStats() {
    return apiOptimizer.getStats();
  }

  /**
   * Clear API optimization caches
   */
  clearOptimizationCaches() {
    apiOptimizer.clearCaches();
  }

  /**
   * Get network performance statistics
   * @returns {Object} Network performance statistics
   */
  getNetworkStats() {
    return apiOptimizer.network.getStats();
  }

  /**
   * Calculate optimal page size for current conditions
   * @param {number} viewportHeight - Viewport height in pixels
   * @param {number} itemHeight - Average item height in pixels
   * @returns {number} Optimal page size
   */
  calculateOptimalPageSize(viewportHeight, itemHeight = 100) {
    return apiOptimizer.pagination.calculateOptimalPageSize(
      viewportHeight,
      itemHeight,
      apiOptimizer.network.getNetworkSpeed()
    );
  }

  /**
   * Check if prefetching should occur
   * @param {number} scrollPosition - Current scroll position (0-1)
   * @param {boolean} hasMore - Whether more data is available
   * @param {boolean} isLoading - Whether currently loading
   * @returns {boolean} Whether to prefetch
   */
  shouldPrefetch(scrollPosition, hasMore, isLoading) {
    return apiOptimizer.pagination.shouldPrefetch(
      scrollPosition,
      hasMore,
      isLoading
    );
  }
}

// Export singleton instance
const threadApiInstance = new ThreadAPI();
export default threadApiInstance;
